import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import error404 from 'assets/404_warning_icon.png';
import { useAuthContext } from 'context/authContext';
import { NewDimItemData } from 'models/response/blockOutput.interface';
import { STATUS } from 'projectConstants';
import { getActiveTabIndex, getBlockDetails, setActiveTabIndexVersion2 } from 'redux/PlanPageSlice';
import {
  getChartLabels,
  getCompareChartType,
  scenarioChartOutputStatus,
  setCompareChartType,
} from 'redux/ScenarioSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import CompareChart from '../../Plan/Compare/CompareChart';
import {
  selectActiveCompareSeries,
  selectCompareDefaultTabIndex,
  selectCompareSafeActiveIndex,
} from '../planPageRevampSelectors';
import {
  PLAN_CARD_INNER_RADIUS,
  PLAN_CARD_PADDING,
  PLAN_CHART_VIEW_HEIGHT,
  PLAN_TABLE_CELL_BORDER,
  PLAN_TEXT,
  text as textToken,
} from '../planPageRevampUi';
import { buildChartImageFileName, downloadChartImage } from '../planChartDownload';

import '../planChartSlider.css';
import PlanCard from './PlanCard';
import PlanChartSkeleton from './PlanChartSkeleton';
import PlanChartToolbar from './PlanChartToolbar';

import type { ChartTypeRegistry } from 'chart.js';
import type { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';

interface PlanCompareChartViewProperties {
  chartType: string;
  chartData: NewDimItemData[];
  chartLabels: string[];
  tabData: unknown;
  activeTabIndex: number;
  onChartTypeChange: (value: string) => void;
  onDownload: () => void;
  isExporting: boolean;
  fillHeight?: boolean;
  chartAreaHeight?: string;
  chartExportReference?: React.MutableRefObject<ChartJSOrUndefined<keyof ChartTypeRegistry> | null>;
}

const PlanCompareChartView = memo(
  ({
    chartType,
    chartData,
    chartLabels,
    tabData,
    activeTabIndex,
    onChartTypeChange,
    onDownload,
    isExporting,
    fillHeight = false,
    chartAreaHeight,
    chartExportReference,
  }: PlanCompareChartViewProperties) => {
    const resolvedChartHeight = chartAreaHeight ?? PLAN_CHART_VIEW_HEIGHT;

    return (
      <>
        <PlanChartToolbar
          viewMode={chartType || 'bar'}
          onChange={onChartTypeChange}
          onDownload={onDownload}
          isExporting={isExporting}
        />

        <Box
          flex={fillHeight ? '1' : undefined}
          minH={fillHeight ? 0 : undefined}
          flexShrink={0}
          display='flex'
          flexDirection='column'
          w='100%'
          p={PLAN_CARD_PADDING}
          overflow={fillHeight ? 'hidden' : 'visible'}
        >
          <Box
            flex={fillHeight ? '1' : undefined}
            h={fillHeight ? undefined : resolvedChartHeight}
            minH={fillHeight ? 0 : resolvedChartHeight}
            border='1px solid'
            borderColor={PLAN_TABLE_CELL_BORDER}
            borderRadius={PLAN_CARD_INNER_RADIUS}
            p={3}
            overflow='hidden'
          >
            <CompareChart
              hideToolbar
              variant='revamp'
              chartData={chartData}
              chartLabels={chartLabels}
              tabData={tabData}
              activeTabIndex={activeTabIndex}
              exportRef={chartExportReference}
              plotHeight={fillHeight ? '100%' : resolvedChartHeight}
              fillPlotHeight={fillHeight}
            />
          </Box>
        </Box>
      </>
    );
  },
);

PlanCompareChartView.displayName = 'PlanCompareChartView';

const toChartData = (series: any): NewDimItemData[] =>
  series.dim_item_data ?? [{ value: series.total_values, name: 'Total' }];

interface PlanCompareChartPanelProperties {
  fillHeight?: boolean;
  chartAreaHeight?: string;
  onToggleAuthModal: () => void;
}

const PlanCompareChartPanel = ({
  fillHeight = false,
  chartAreaHeight,
  onToggleAuthModal,
}: PlanCompareChartPanelProperties) => {
  const { user } = useAuthContext();
  const dispatch: AppDispatch = useDispatch();
  const chartExportReference = useRef<ChartJSOrUndefined<keyof ChartTypeRegistry> | null>(null);
  const activeSeries = useSelector(selectActiveCompareSeries);
  const safeActiveIndex = useSelector(selectCompareSafeActiveIndex);
  const defaultTabIndex = useSelector(selectCompareDefaultTabIndex);
  const activeTabIndex = useSelector(getActiveTabIndex);
  const tabsDataLength = useSelector((state: any) => state.scenario.tabsData?.length ?? 0);
  const chartLabels = useSelector(getChartLabels);
  const chartStatus = useSelector(scenarioChartOutputStatus);
  const chartType = useSelector(getCompareChartType);
  const blockDetails = useSelector(getBlockDetails);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (activeTabIndex === -1 && tabsDataLength > 0) {
      dispatch(setActiveTabIndexVersion2(defaultTabIndex));
    }
  }, [activeTabIndex, defaultTabIndex, dispatch, tabsDataLength]);

  const chartData = useMemo(() => (activeSeries ? toChartData(activeSeries) : []), [activeSeries]);

  const onChartTypeChange = useCallback(
    (value: string) => {
      dispatch(setCompareChartType(value));
    },
    [dispatch],
  );

  const onDownload = useCallback(async () => {
    if (user?.is_anonymous) {
      onToggleAuthModal();
      return;
    }

    setIsExporting(true);
    try {
      const fileName = buildChartImageFileName(blockDetails?.name);
      downloadChartImage(chartExportReference.current, fileName);
    } finally {
      setIsExporting(false);
    }
  }, [blockDetails?.name, onToggleAuthModal, user?.is_anonymous]);

  const hasCachedChart = tabsDataLength > 0;
  const cardFlex = fillHeight ? '1 1 0' : undefined;
  const cardMinH = fillHeight ? 0 : undefined;

  if (
    (chartStatus === STATUS.LOADING && !hasCachedChart) ||
    (chartStatus === STATUS.IDLE && tabsDataLength === 0)
  ) {
    return (
      <PlanCard flex={cardFlex} minH={cardMinH}>
        <PlanChartSkeleton fillHeight={fillHeight} chartAreaHeight={chartAreaHeight} />
      </PlanCard>
    );
  }

  if (tabsDataLength === 0 && chartStatus === STATUS.FAILED) {
    return (
      <PlanCard flex={cardFlex} minH={cardMinH}>
        <Flex direction='column' gap={2} alignItems='center' py={8}>
          <Image src={error404} alt='error icon' boxSize={40} />
          <Text {...PLAN_TEXT.body} color={textToken.secondary}>
            Something went wrong, Please contact support@blox.so
          </Text>
        </Flex>
      </PlanCard>
    );
  }

  if (tabsDataLength === 0 && chartStatus === STATUS.SUCCESS) {
    return (
      <PlanCard flex={cardFlex} minH={cardMinH}>
        <Flex h='100%' minH='280px' alignItems='center' justifyContent='center'>
          <Text {...PLAN_TEXT.body} color={textToken.secondary}>
            No chart data available
          </Text>
        </Flex>
      </PlanCard>
    );
  }

  if (!activeSeries || !chartLabels?.length) {
    return (
      <PlanCard flex={cardFlex} minH={cardMinH}>
        <PlanChartSkeleton fillHeight={fillHeight} chartAreaHeight={chartAreaHeight} />
      </PlanCard>
    );
  }

  return (
    <PlanCard flex={cardFlex} minH={cardMinH}>
      <PlanCompareChartView
        chartType={chartType}
        chartData={chartData}
        chartLabels={chartLabels}
        tabData={activeSeries}
        activeTabIndex={safeActiveIndex}
        onChartTypeChange={onChartTypeChange}
        onDownload={onDownload}
        isExporting={isExporting}
        fillHeight={fillHeight}
        chartAreaHeight={chartAreaHeight}
        chartExportReference={chartExportReference}
      />
    </PlanCard>
  );
};

export default memo(PlanCompareChartPanel);
